import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { formatVideoTime } from '../core/time';
import { Button, Timeline, type TimelineClip } from './index';

export interface PlayerHandle {
  seek: (t: number) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  time: () => number;
  playing: () => boolean;
  /** Plays [start, end) and stops at end. */
  playRange: (start: number, end: number) => void;
}

interface Props {
  url: string;
  duration: number;
  rate?: number;
  clips?: TimelineClip[];
  markers?: { at: number; label: string }[];
  topLeft?: (t: number) => ReactNode;
  topRight?: (t: number) => ReactNode;
  onTime?: (t: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  /** Extra rows under the transport controls. */
  children?: ReactNode;
  /** Right side of the transport row. */
  transportEnd?: ReactNode;
  compact?: boolean;
}

export const Player = forwardRef<PlayerHandle, Props>(function Player(
  { url, duration, rate = 1, clips, markers, topLeft, topRight, onTime, onPlayingChange, children, transportEnd, compact },
  ref,
) {
  const video = useRef<HTMLVideoElement>(null);
  const stopAt = useRef<number | null>(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;

  const sync = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (stopAt.current !== null && v.currentTime >= stopAt.current) {
      v.pause();
      stopAt.current = null;
    }
    setT(v.currentTime);
    onTimeRef.current?.(v.currentTime);
  }, []);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      sync();
      raf = requestAnimationFrame(loop);
    };
    if (playing) raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, sync]);

  useEffect(() => {
    if (video.current) video.current.playbackRate = rate;
  }, [rate]);

  useEffect(() => onPlayingChange?.(playing), [playing, onPlayingChange]);

  const seek = useCallback(
    (to: number) => {
      const v = video.current;
      if (!v) return;
      v.currentTime = Math.max(0, Math.min(duration || v.duration || 0, to));
      sync();
    },
    [duration, sync],
  );

  useImperativeHandle(
    ref,
    () => ({
      seek,
      play: () => void video.current?.play().catch(() => undefined),
      pause: () => video.current?.pause(),
      toggle: () => {
        const v = video.current;
        if (!v) return;
        stopAt.current = null;
        if (v.paused) void v.play().catch(() => undefined);
        else v.pause();
      },
      time: () => video.current?.currentTime ?? 0,
      playing: () => !!video.current && !video.current.paused,
      playRange: (start, end) => {
        const v = video.current;
        if (!v) return;
        v.currentTime = start;
        stopAt.current = end;
        void v.play().catch(() => undefined);
      },
    }),
    [seek],
  );

  const step = (d: number) => seek((video.current?.currentTime ?? 0) + d);

  return (
    <section className="player" aria-label="Match player">
      <div className="player-stage" style={compact ? { aspectRatio: '16 / 9' } : undefined}>
        <video
          ref={video}
          src={url}
          preload="auto"
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => {
            setPlaying(false);
            sync();
          }}
          onSeeked={sync}
          onLoadedMetadata={sync}
          onClick={() => {
            const v = video.current;
            if (v) v.paused ? void v.play() : v.pause();
          }}
        />
        {topLeft && <div className="player-overlay-tl">{topLeft(t)}</div>}
        {topRight && <div className="player-overlay-tr">{topRight(t)}</div>}
      </div>
      <div className="player-controls">
        <Timeline duration={duration} clips={clips} markers={markers} playhead={t} onSeek={seek} label={`${clips?.length ?? 0} clips on the match timeline`} />
        <input
          className="player-range"
          type="range"
          min={0}
          max={duration || 1}
          step={0.1}
          value={Math.min(t, duration || t)}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="Playhead"
          aria-valuetext={formatVideoTime(t)}
        />
        <div className="app-row" style={{ flexWrap: 'wrap' }}>
          <Button variant="overlay" size="sm" icon={playing ? 'pause' : 'play'} iconOnly aria-label={playing ? 'Pause' : 'Play'} onClick={() => (video.current?.paused ? void video.current.play() : video.current?.pause())} />
          <Button variant="overlay" size="sm" onClick={() => step(-10)} aria-label="Back 10 seconds">
            −10 s
          </Button>
          <Button variant="overlay" size="sm" onClick={() => step(-1)} aria-label="Back 1 second">
            −1 s
          </Button>
          <Button variant="overlay" size="sm" onClick={() => step(1)} aria-label="Forward 1 second">
            +1 s
          </Button>
          <Button variant="overlay" size="sm" onClick={() => step(10)} aria-label="Forward 10 seconds">
            +10 s
          </Button>
          <span className="player-time" style={{ marginLeft: 8 }}>
            {formatVideoTime(t)} <small>/ {formatVideoTime(duration)}</small>
          </span>
          <span style={{ flexGrow: 1 }} />
          {transportEnd}
        </div>
        {children}
      </div>
    </section>
  );
});
